import React from 'react';
import axios from 'axios';
import CarImage, { CarManufacturer } from './table-components';
import { AnyObject } from 'common/interfaces';
import { API_ENDPOINT, isMobileWidth } from '../../constants';
import { emtyFilterState, emtyFilterStateForServer } from './my-vehicles';
import { VehicleStatusesLabel } from './my-vehicles.props';
import { DriverCell, DriverStatus } from './table-components';
import authService from 'services/auth-service';
import { ColumnsVariant, ColumnType } from '../../components/table-component/table-component.props';
import { FIRST_COLUMN_IMAGE_DEFAULT_WIDTH } from 'components/table-component/table-component';
import { CRM_VALUE } from 'pages/my-vehicles/my-vehicles';
import {
  StateCodeLabelEnum,
  StateCodeValueEnum
} from 'components/driving-license-control-component/driving-license-control.props';
import VehicleCell from './table-components/vehicle-cell/vehicle-cell';
import { ConsumptionEstimate } from './table-components/consumption-estimate/consumption-estimate';
import { CarClass } from './table-components/car-class/car-class';
import { DataQuality } from './table-components/data-quality/data-quality';
import { RecommendedMileage } from './table-components/recommended-mileage/recommended-mileage';

export const textTransformer = (
  delimiter: string,
  from: 'start' | 'end',
  numberOfValues: number,
  capitalLetter: boolean,
  currentValue: string
): string => {
  if (delimiter === '') {
    return currentValue;
  }
  let splittedValue = currentValue.split(delimiter);
  let transformedValue = '';

  if (from === 'start') {
    transformedValue = splittedValue.slice(0, splittedValue.length - numberOfValues).join(' ');
  } else {
    transformedValue = splittedValue.reverse().slice(0, numberOfValues).reverse().join(' ');
  }

  if (capitalLetter) {
    return `${transformedValue[0].toUpperCase()}${transformedValue.slice(1)}`;
  }
  return transformedValue;
};

export const deactivatingCar = async (id: string, reason: string, description: string) => {
  const token = await authService.acquireTokenAsync();
  return axios({
    method: 'POST',
    url: `${API_ENDPOINT}/api/fuhrpark/deactivate`,
    headers: {
      Authorization: `Bearer ${token}`
    },
    data: {
      id,
      reason,
      description
    }
  });
};

export const getDefaultFilterState = () => {
  let dataForServer: AnyObject = {
    ...emtyFilterStateForServer,
    [CRM_VALUE.stateCode]: [StateCodeValueEnum.Active]
  };

  let dataForClient: AnyObject = {
    ...emtyFilterState,
    [CRM_VALUE.stateCode]: [StateCodeLabelEnum.Active]
  };

  if (isMobileWidth) {
    dataForServer = emtyFilterStateForServer;
    dataForClient = emtyFilterState;
  }

  return {
    dataForServer,
    dataForClient
  };
};

export const getChipColor = (status: VehicleStatusesLabel) => {
  if (status === VehicleStatusesLabel.Ordered || status === VehicleStatusesLabel.InOperation) {
    return 'green';
  }
  if (status === VehicleStatusesLabel.InControl || status === VehicleStatusesLabel.ControlRequested) {
    return 'darkGold';
  }

  if (status === VehicleStatusesLabel.Controlled) {
    return 'grey600';
  }

  return 'grey300';
};

export const getColumns = (): ColumnType[] => [
  {
    name: 'Fahrzeugbild',
    type: ColumnsVariant.Image,
    component: <CarImage />,
    sort: false,
    width: FIRST_COLUMN_IMAGE_DEFAULT_WIDTH,
    columnId: 'carImage'
  },
  {
    name: 'Mobexo Car-ID',
    type: ColumnsVariant.Text,
    propName: 'uds_modexocarinternalid',
    sort: true,
    sortName: 'uds_modexocarinternalid',
    width: '130px',
    columnId: 'mobexoCarId'
  },
  {
    name: 'Unternehmen',
    type: ColumnsVariant.Text,
    propName: 'new_sparteid',
    sort: true,
    sortName: 'new_sparteid',
    width: '130px',
    columnId: 'companyId'
  },
  {
    name: 'Standort',
    type: ColumnsVariant.Text,
    propName: 'new_standortid',
    sort: true,
    sortName: 'new_standortid',
    width: '130px',
    columnId: 'location'
  },
  {
    name: 'Kennzeichen',
    type: ColumnsVariant.Text,
    component: <VehicleCell />,
    propName: 'new_name',
    sort: true,
    sortName: 'new_name',
    width: '130px',
    columnId: 'carNumber'
  },
  {
    name: 'Fahrzeug',
    type: ColumnsVariant.Text,
    component: <CarManufacturer />,
    sort: false,
    width: '130px',
    columnId: 'carManufacturer'
  },
  {
    name: 'Erstzulassung',
    type: ColumnsVariant.Date,
    propName: 'new_ezl',
    sort: true,
    sortName: 'new_ezl',
    width: '130px',
    columnId: 'firstRegistration'
  },
  {
    name: 'Kostenstelle',
    type: ColumnsVariant.Text,
    propName: 'new_kostenstelleid',
    sort: true,
    sortName: 'new_kostenstelleid',
    width: '130px',
    columnId: 'costCenter'
  },
  {
    name: 'Fahrer',
    type: ColumnsVariant.Text,
    propName: 'new_fahrerid',
    component: <DriverCell />,
    sort: false,
    width: '130px',
    columnId: 'driver'
  },
  {
    name: 'Leasingrückgabe',
    type: ColumnsVariant.Date,
    propName: 'uds_lease_return_date',
    sort: true,
    sortName: 'uds_lease_return_date',
    width: '130px',
    columnId: 'leaseReturn'
  },
  {
    name: 'LVN',
    type: ColumnsVariant.Text,
    propName: 'new_lvn',
    sort: true,
    sortName: 'new_lvn',
    width: '130px',
    columnId: 'lvn'
  },
  {
    name: 'VIN',
    type: ColumnsVariant.Text,
    propName: 'new_fin',
    sort: true,
    sortName: 'new_fin',
    width: '130px',
    columnId: 'vinCode'
  },
  {
    name: 'Status',
    type: ColumnsVariant.Chip,
    component: <DriverStatus />,
    propName: 'new_status_carcode',
    sort: true,
    sortName: 'new_status_carcode',
    width: '130px',
    columnId: 'status'
  },
  {
    name: 'Pool-Car Klasse',
    type: ColumnsVariant.Text,
    component: <CarClass />,
    propName: 'uds_poolcar_classid',
    sort: true,
    sortName: 'uds_poolcar_classid',
    width: '130px',
    columnId: 'poolCarKlasse'
  },
  {
    name: 'Datenqualität',
    type: ColumnsVariant.Text,
    component: <DataQuality />,
    propName: 'uds_date_quality_statecode',
    sort: true,
    sortName: 'uds_date_quality_statecode',
    width: '130px',
    columnId: 'dateQualityStatecode'
  },
  {
    name: 'Laufleistungempfehlung',
    type: ColumnsVariant.Text,
    component: <RecommendedMileage />,
    propName: 'uds_mileagerecommendation',
    sort: true,
    sortName: 'uds_mileagerecommendation',
    width: '130px',
    columnId: 'recommendedMileage'
  },
  {
    name: 'Verbrauch',
    type: ColumnsVariant.Text,
    component: <ConsumptionEstimate />,
    propName: 'uds_consumption_estimate_value',
    sort: true,
    sortName: 'uds_consumption_estimate_value',
    width: '130px',
    columnId: 'consumptionEstimate'
  }
];
